import { newItem } from "./catalog.item";
import { getDefaultProductTemplateRoot } from "./catalog.func";
import {
  ALL_PRODUCTS,
  INDIVIDUAL_PRODUCTS,
  initProductContainerCollection,
  isBoundContainer,
  isExplicitProductList,
} from "./catalog.products";
import { getUnique } from "../../services/util";
import { gridSize, templateGridSize } from "../design/designer";
import {
  getAllLayouts,
  getLayoutItems,
  injectLayoutItems,
} from "../layouts/layouts";
import { CatalogBuilder } from "./catalog.builder";
import {
  CatalogGrid,
  PageItemConfig,
  CatalogItemRef,
  LayoutSetItem,
  GridItem,
} from "./catalogs";
import { trackEvent } from "../analytics/analytics";

/**
 * Parse grid name into base name and number
 * Examples:
 *   "My Products"     -> { base: "My Products", number: null }
 *   "My Products 1"   -> { base: "My Products", number: 1 }
 *   "Grid 5"          -> { base: "Grid", number: 5 }
 */
const parseGridName = (
  name: string,
): { base: string; number: number | null } => {
  if (!name) return { base: "Grid", number: null };

  // Match pattern: "Name 123" or "Name123" at end of string
  const match = name.match(/^(.*?)\s*(\d+)$/);

  if (match) {
    return {
      base: match[1].trim() || "Grid",
      number: parseInt(match[2], 10),
    };
  }

  return { base: name.trim(), number: null };
};

/**
 * Generate next available grid name with smart numbering
 * Examples:
 *   "My Products"  + existing ["My Products 1"] -> "My Products 2"
 *   "Grid 1"       + existing ["Grid 2", "Grid 5"] -> "Grid 6"
 *   "Products"     + existing [] -> "Products 1"
 */
export const getNextGridName = (
  baseName: string,
  existingGrids: CatalogGrid[],
): string => {
  const { base: originalBase, number: originalNumber } =
    parseGridName(baseName);

  // Find all grids with same base name
  const existingNumbers = existingGrids
    .map((g) => {
      const gridName = g.name || g.title || "";
      const parsed = parseGridName(gridName);
      if (parsed.base === originalBase && parsed.number !== null) {
        return parsed.number;
      }
      return null;
    })
    .filter((n) => n !== null) as number[];

  // Find highest number used (or use original number if higher)
  const maxNumber =
    existingNumbers.length > 0
      ? Math.max(...existingNumbers)
      : originalNumber || 0;

  // Return next number
  return `${originalBase} ${maxNumber + 1}`;
};

/**
 * How many products this grid actually puts on the page.
 *
 * `previewProductIds` is the BINDING's membership, so it is the answer only for a bound grid.
 * For a grid carrying an explicit list it reported the source category's count (24) while the
 * grid held 22, and the viewer, the designer and the Concierge preview each said something
 * different. Read the explicit list first whenever there is one.
 */
export const getGridProductCount = (grid?: CatalogGrid): number => {
  if (!grid) return 0;
  if (isExplicitProductList(grid))
    return (
      grid.products?.length || grid.items?.length || grid.gridItems?.length || 0
    );
  return (
    grid.previewProductIds?.length ||
    grid.gridItems?.length ||
    grid.items?.length ||
    0
  );
};

export const newGrid = (): CatalogGrid => {
  return {
    title: "+ New Product Grid",
    id: 0,
    w: 2,
    h: 2,
    items: [],
    products: [],
    isNew: true,
  };
};

export const refreshGrids = (builder: CatalogBuilder) => {
  const grids: CatalogGrid[] = [];
  let g: CatalogGrid = null;
  let index = 0;
  let gridId = 1;

  builder.pages.forEach((page) => {
    if (
      page.pageItem?.di.PageType === "products" ||
      page.pageItem?.di.PageType === "collection"
    ) {
      const isCollection = page.pageItem.di.PageType === "collection";
      const config: PageItemConfig = page.pageItem?.info?.config || {};
      let templateId = config.templateId;
      let items: CatalogItemRef[] = [];
      let item: CatalogItemRef;

      if (!isCollection) {
        items = getUnique(
          page.items.filter((e) => !!e.info.product),
          (i) => i.di.ItemId,
        ).sort((a, b) => {
          // Sort by y first (with 5px tolerance for row alignment), then by x
          // This matches the order used in injectLayoutItems during generation
          return (
            Math.round((a.di.y - b.di.y) / 5) * 5 * 100 + (a.di.x - b.di.x)
          );
        });
        item = items[0];
        if (item && !templateId && !config.gridId) {
          templateId = item.di.TemplateId;
        }
      }
      const w = config.useLayout
        ? 1
        : gridSize / config.w ||
          (item?.di.w > 0 ? Math.floor(templateGridSize / item.di.w) : null);
      const h = config.useLayout
        ? 1
        : gridSize / config.h ||
          (item?.di.h > 0 ? Math.floor(templateGridSize / item.di.h) : null);

      if (
        g &&
        (isCollection ||
          (!isCollection && g.collection) ||
          (config.gridName && config.gridName !== g.name) ||
          (page.pageItem.di.Title &&
            page.pageItem.di.Title !== g.sectionTitle) ||
          g.templateId !== templateId ||
          (!g.useLayout && (g.w !== w || g.h !== h)) ||
          (g.useLayout && config.layoutSet !== g.layoutSet))
      ) {
        grids.push(g);
        g = null;
      }
      if (g) {
        g.end = page.pageId;
        g.items = getUnique(g.items.concat(items), (i) => i.di.ItemId);
      } else {
        const id = gridId++;
        const existingGrid = builder.grids.find((g) => g.id === id);

        g = {
          id,
          name: config.gridName || "",
          useLayout: config.useLayout,
          layoutSet: config.layoutSet,
          layouts: [],
          templateId: templateId,
          collectionId:
            config.collectionId ??
            (config.productSource === ALL_PRODUCTS
              ? ALL_PRODUCTS
              : config.categoryId
              ? null
              : INDIVIDUAL_PRODUCTS),
          categoryId: config.categoryId,
          productSource: config.productSource,
          inStock: config.inStock,
          w: w || 2,
          h: h || 2,
          begin: page.pageId,
          end: page.pageId,
          type: page.pageItem.di.PageType,
          sectionTitle: page.pageItem.di.Title,
          items: items,
          index: index++,
          products: [],
          expanded: existingGrid?.expanded ?? false,
          pageItem: page.pageItem,
        };
        if (g.templateId) {
          g.template = builder.data.templates.find(
            (t) => t.template.id === g.templateId,
          );
        }
        initProductContainerCollection(builder.data.productDb, g);
        initGridLayoutSet(builder, g);
      }
    } else if (g) {
      grids.push(g);
      g = null;
    }
  });

  if (g) {
    grids.push(g);
  }

  grids.push(newGrid());

  grids.forEach((g) => {
    g.products = g.items.map((i) => i.di.ItemId);
    // The container was initialised above, before the pages had been walked and the item list
    // was known, so an explicit-list grid resolved to an empty preview. Re-point it now.
    if (isExplicitProductList(g)) g.previewProductIds = g.products;

    if (g.title) return;
    g.title = g.name || g.sectionTitle;
    if (!g.title && g.collection) g.title = g.collection.group.name;
    if (!g.title && !g.useLayout)
      g.title =
        (g.template ? g.template.template.name : "Default") +
        " [" +
        g.w +
        "x" +
        g.h +
        "]";
    if (!g.title && g.useLayout && g.layouts?.length > 0)
      g.title = g.layouts[0].layout.name;
    g.title = g.title || "Grid " + g.id;
    //+ " [p. " + g.begin + (g.end > g.begin ? " - " + g.end : "") + "]";
  });

  return grids;
};

export const initGridLayout = (
  builder: CatalogBuilder,
  grid: CatalogGrid,
  layoutId: string,
  role?: string,
) => {
  if (!grid.useLayout) return;
  grid.layouts = grid.layouts || [];
  const l = getAllLayouts(builder.data.layoutDb).find((l) => {
    return l.layout.id === layoutId;
  });
  if (l) {
    //initLayout(builder, l);
    const layout = grid.layouts.find((l) => l.role === role);
    if (layout) {
      layout.layout = l.layout;
      //layout.items = l.items;
    } else {
      grid.layouts.push({
        role: role,
        layout: l.layout,
        //items: l.items,
      });
    }
  }
};

const initGridLayoutSet = (builder: CatalogBuilder, g: CatalogGrid) => {
  if (!g.useLayout) return;
  g.layouts = [];
  if (g.layoutSet) {
    const layoutSet = JSON.parse(g.layoutSet) as LayoutSetItem[];
    layoutSet.forEach((ls) => {
      initGridLayout(builder, g, ls.layoutId, ls.role);
    });
  }
};

const getGridItem = (
  builder: CatalogBuilder,
  grid: CatalogGrid,
  productId?: string,
): GridItem => {
  return {
    product: productId
      ? builder.data.productDb.productMap.get(productId)
      : null,
    template: grid.template,
    w: gridSize / grid.w,
    h: gridSize / grid.h,
  };
};

const addPagesWithTemplate = (
  builder: CatalogBuilder,
  grid: CatalogGrid,
  isPreview: boolean,
  pageId: number,
  config: PageItemConfig,
  parentColor?: string,
) => {
  grid.preview = {
    pages: [],
    pageId,
  };

  let items = grid.gridItems;
  if (!items) {
    if (grid.templateId) {
      if (grid.template?.template.id !== grid.templateId || !grid.template)
        grid.template = builder.data.templates.find(
          (t) => t.template.id === grid.templateId,
        );
    } else {
      grid.template = getDefaultProductTemplateRoot(builder.data.templates);
      grid.templateId = grid.template?.template.id;
    }

    initProductContainerCollection(builder.data.productDb, grid);

    items = grid.previewProductIds.map((id) => getGridItem(builder, grid, id));
  }

  let x = 0,
    y = 0,
    cy = 0,
    pos = 0;
  const scale = templateGridSize / gridSize;

  let page = null;

  for (let i = 0; i < items.length; i++) {
    const p = items[i];
    const h = p.h || (p.template ? p.template.template.height : 2);
    const w = p.w || (p.template ? p.template.template.width : 2);
    if (x + w > gridSize) {
      x = 0;
      y = cy;
    }
    if (!page || y + h > gridSize) {
      if (page) {
        pageId++;
      }
      page = builder.addPage(
        pageId,
        isPreview,
        isBoundContainer(grid) ? "collection" : "products",
        config,
      );
      if (!page) {
        return pageId;
      }
      // Inherit background color from grid config page (V1 parity: AssignParentPageConfig)
      if (parentColor && page.pageItem && !page.pageItem.di.Color) {
        page.pageItem.di.Color = parentColor;
      }
      grid.preview.pages.push(page);
      y = 0;
      x = 0;
      cy = 0;
      pos = 0;
    }

    const item: CatalogItemRef = newItem({
      PageId: pageId,
      Position: pos++,
      Type: "template",
      ItemId: p.product?.product.id,
      TemplateId: p.template?.template.id,
      x: x * scale,
      y: y * scale,
      w: w * scale,
      h: h * scale,
    });
    if (!isPreview) builder.items.push(item);

    item.info = { template: p.template, product: p.product };

    builder.initItem(item);
    page.items.push(item);

    if (x > 0 && x + w === gridSize && y + h < cy) {
      y = y + h;
    } else {
      x = x + w;
    }
    cy = Math.max(cy, y + h);
  }
  return pageId - 1;
};

const addPagesWithLayout = (
  builder: CatalogBuilder,
  grid: CatalogGrid,
  isPreview: boolean,
  pageId: number,
  config: PageItemConfig,
  parentColor?: string,
) => {
  grid.preview = {
    pages: [],
    pageId,
  };

  if (!grid.layouts?.length) return pageId;

  initProductContainerCollection(builder.data.productDb, grid);
  const products = grid.previewProductIds.map((id) =>
    builder.data.productDb.productMap.get(id),
  );
  grid.layouts.forEach((l) => {
    l.items = getLayoutItems(l.layout);
  });
  let last = products.length + 1;
  let i = 0;
  let injected = 0;
  let injectedPages = 0;
  while (products.length && products.length < last) {
    const page = builder.addPage(
      pageId,
      isPreview,
      isBoundContainer(grid) ? "collection" : "products",
      config,
    );
    if (!page) return pageId;
    // Inherit background color from grid config page (V1 parity: AssignParentPageConfig)
    if (parentColor && page.pageItem && !page.pageItem.di.Color) {
      page.pageItem.di.Color = parentColor;
    }
    last = products.length;
    const l = grid.layouts[i++ % grid.layouts.length];
    injectedPages++;
    injected += injectLayoutItems(builder, page, {
      x: 0,
      y: 0,
      kw: 1,
      kh: 1,
      products,
      items: l.items,
      isPreview,
    });
    pageId++;
    grid.preview.pages.push(page);
  }
  if (!isPreview) {
    trackEvent({
      type: "trace",
      category: "grid",
      action: "inject",
      label: `layout items: ${injected}, pages: ${injectedPages} to page ${
        pageId - 1
      }`,
      internal: true,
    });
  }
  return pageId - 1;
};

const createGridPages = (
  builder: CatalogBuilder,
  grid: CatalogGrid,
  isPreview: boolean,
  pageId = 1,
) => {
  const layoutSet =
    grid.useLayout && grid.layouts?.length
      ? JSON.stringify(
          grid.layouts.map((l) => ({
            layoutId: l.layout?.id,
            role: l.role,
          })) as LayoutSetItem[],
        )
      : null;

  const config: PageItemConfig = {
    ...grid.pageItem?.info.config,
    templateId: grid.useLayout ? null : grid.templateId,
    w: gridSize / grid.w,
    h: gridSize / grid.h,
    gridName: grid.name,
    gridId: grid.id,
    title: grid.sectionTitle,
    useLayout: grid.useLayout,
    layoutSet,
    inStock: grid.inStock,
    collectionId: grid.collectionId,
    categoryId: grid.categoryId,
    productSource: grid.productSource,
    imgPosition: grid.pageItem?.imgPosition,
  };

  if (config.collectionId === INDIVIDUAL_PRODUCTS) {
    config.collectionId = null;
  }
  if (
    config.collectionId === ALL_PRODUCTS ||
    grid.collectionId === ALL_PRODUCTS
  ) {
    config.collectionId = null;
    config.productSource = ALL_PRODUCTS;
  }
  if (isExplicitProductList(grid)) {
    // Drop the binding this grid was BUILT from: on reload the client would read it back as
    // the grid's product list. Saving from the designer heals a row written before this fix.
    config.collectionId = null;
    config.categoryId = null;
  }

  // Capture grid config page's background color for sub-page inheritance
  const parentColor = grid.pageItem?.di.Color || "";

  if (
    !isPreview &&
    !isExplicitProductList(grid) &&
    ((grid.collectionId && grid.collectionId !== INDIVIDUAL_PRODUCTS) ||
      grid.categoryId)
  ) {
    const page = builder.addPage(pageId, false, "collection", config);
    // Inherit background color if the collection page marker was freshly created
    if (page?.pageItem && parentColor && !page.pageItem.di.Color) {
      page.pageItem.di.Color = parentColor;
    }
    return page?.pageId;
  }
  if (grid.useLayout) {
    return addPagesWithLayout(
      builder,
      grid,
      isPreview,
      pageId,
      config,
      parentColor,
    );
  } else {
    return addPagesWithTemplate(
      builder,
      grid,
      isPreview,
      pageId,
      config,
      parentColor,
    );
  }
};

export const previewGrid = (builder: CatalogBuilder, grid: CatalogGrid) => {
  createGridPages(builder, grid, true);
};

export const deleteGrid = (builder: CatalogBuilder, grid: CatalogGrid) => {
  builder.deletePages(grid.begin, grid.end - grid.begin + 1);
};

export const generateGrid = (builder: CatalogBuilder, grid: CatalogGrid) => {
  if (!grid.isNew) {
    deleteGrid(builder, grid);
  }
  const pageId = createGridPages(builder, grid, false, grid.begin);
  trackEvent({
    type: "trace",
    category: "grid",
    action: "generate",
    label: `create pages: ${grid.begin} - ${grid.end ?? "?"}, page: ${pageId} ${
      grid.title
    } [${grid.useLayout ? "layout" : "template"}] collection: ${
      grid.collectionId
    }`,
    internal: true,
  });
  builder.refreshPages(true);
  builder.setActivePage(grid.begin);
};
